import React from "react";
import { useTranslation } from "react-i18next";
import RiskZoneBadge from "../components/RiskZoneBadge";
import Section from "../components/Section";
import SectionHeading from "../components/SectionHeading";
import { ZONES } from "../landingConfig";

/**
 * Product proof.
 *
 * These are schematic views built in the DOM, not screenshots. That is a
 * deliberate trade: they weigh nothing, cannot shift the layout, stay sharp at
 * any resolution, translate with the rest of the page, and — most importantly —
 * cannot leak real student or school data, which a screenshot of the live app
 * easily could.
 *
 * Every value below is fabricated. Students are shown as bare initials and
 * schools as numbers, so nothing here identifies a real person or institution.
 */
const ROSTER = [
  { id: "1", initials: "А. К.", zone: "green_zone", date: "12.09" },
  { id: "2", initials: "Б. М.", zone: "yellow_zone", date: "14.09" },
  { id: "3", initials: "С. Т.", zone: "red_zone", date: "15.09" },
  { id: "4", initials: "Д. Ж.", zone: "green_zone", date: "15.09" },
  { id: "5", initials: "Е. Н.", zone: "orange_zone", date: "18.09" },
];

/**
 * One student's zone history. Ends on an improvement (orange -> yellow) so the
 * mechanism reads as two-directional rather than a one-way label.
 */
const HISTORY = ["ev1", "ev2", "ev3"];

const ZONE_BY_CODE = ZONES.reduce((acc, z) => ({ ...acc, [z.code]: z }), {});

export default function ProductShots() {
  const { t } = useTranslation();

  return (
    <Section>
      <SectionHeading
        eyebrow={t("landing.shots.eyebrow")}
        title={t("landing.shots.h2")}
        sub={t("landing.shots.sub")}
      />

      <div className="mt-12 grid gap-6 lg:grid-cols-2">
        {/* Class roster */}
        <figure className="landing-reveal m-0 overflow-hidden rounded-xl border border-line bg-white">
          <figcaption className="flex flex-wrap items-baseline justify-between gap-2 border-b border-line px-5 py-4">
            <h3 className="font-semibold text-black-landing">
              {t("landing.shots.roster_title")}
            </h3>
            <span className="text-xs text-muted">{t("landing.shots.note")}</span>
          </figcaption>

          {/* Narrow viewports scroll the table rather than the page. */}
          <div className="overflow-x-auto">
            <table className="w-full min-w-[22rem] border-collapse text-sm">
              <thead>
                <tr className="text-left text-muted">
                  <th scope="col" className="px-5 py-2.5 font-medium">
                    {t("landing.shots.roster_student")}
                  </th>
                  <th scope="col" className="px-5 py-2.5 font-medium">
                    {t("landing.shots.roster_zone")}
                  </th>
                  <th scope="col" className="px-5 py-2.5 font-medium">
                    {t("landing.shots.roster_updated")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {ROSTER.map(({ id, initials, zone, date }) => (
                  <tr key={id} className="border-t border-line">
                    <td className="px-5 py-3 font-medium text-black-landing">
                      {initials}
                    </td>
                    <td className="px-5 py-3">
                      <RiskZoneBadge zone={ZONE_BY_CODE[zone]} size="sm" />
                    </td>
                    <td className="px-5 py-3 tabular-nums text-muted">{date}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </figure>

        {/* One student's zone history — makes the state machine concrete. */}
        <figure className="landing-reveal m-0 overflow-hidden rounded-xl border border-line bg-white">
          <figcaption className="flex flex-wrap items-baseline justify-between gap-2 border-b border-line px-5 py-4">
            <h3 className="font-semibold text-black-landing">
              {t("landing.shots.history_title")}
            </h3>
            <span className="text-xs text-muted">{t("landing.shots.note")}</span>
          </figcaption>

          <ol className="flex flex-col gap-4 px-5 py-5">
            {HISTORY.map((key) => (
              <li
                key={key}
                className="flex flex-col gap-1 border-l-2 border-line pl-3"
              >
                <span className="text-xs tabular-nums text-muted">
                  {t(`landing.shots.${key}_date`)}
                </span>
                {/* The zone-to-zone arrow is part of the translated string. */}
                <span className="text-sm font-medium text-black-landing">
                  {t(`landing.shots.${key}_move`)}
                </span>
                <span className="text-sm leading-relaxed text-muted">
                  {t(`landing.shots.${key}_reason`)}
                </span>
              </li>
            ))}
          </ol>
        </figure>
      </div>
    </Section>
  );
}
